import { useState, useMemo, useEffect } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Clock } from "lucide-react";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { logActivity } from "@/lib/activity-log";
import { useFamily } from "@/hooks/useFamily";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { getDateString } from "@/lib/family-date";
import { RecurrenceInput } from "@/components/shared/RecurrenceInput";
import { DateTimeInput } from "@/components/shared/DateTimeInput";

interface ChildOption {
  id: string;
  name: string;
  avatar_emoji: string;
  family_id: string;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  childId: string;
  familyId: string;
  allChildren?: ChildOption[];
}

const CATEGORIES = [
  { value: "info", label: "Beskjed", emoji: "💬" },
  { value: "take_along", label: "Ta med", emoji: "🎒" },
  { value: "deadline", label: "Frist", emoji: "⏰" },
];

const DAY_LABELS = ["Man", "Tir", "Ons", "Tor", "Fre", "Lør", "Søn"];

type RepeatType = "none" | "daily" | "weekly";

function getDaysForRepeatLegacy(type: RepeatType, weeklyDays: number[]): number[] {
  if (type === "daily") return [1, 2, 3, 4, 5, 6, 7];
  if (type === "weekly") return weeklyDays;
  return [];
}

function buildRepeatRule(
  repeatType: RepeatType,
  weeklyDays: number[],
  hasTime: boolean,
  dayTimes: Record<number, string>,
  graceMinutes: number,
): string | null {
  if (repeatType === "none") return null;
  const days = getDaysForRepeatLegacy(repeatType, weeklyDays);
  if (days.length === 0) return null;

  const rule: Record<string, unknown> = {
    type: repeatType === "daily" ? "daily" : "weekly",
  };
  if (repeatType !== "daily") rule.days = days;

  if (hasTime) {
    const uniqueTimes = [...new Set(days.map((d) => dayTimes[d] || "08:00"))];
    if (uniqueTimes.length === 1) {
      rule.time = uniqueTimes[0];
    } else {
      rule.times = Object.fromEntries(days.map((d) => [d, dayTimes[d] || "08:00"]));
    }
    rule.graceMinutes = graceMinutes;
  }

  return JSON.stringify(rule);
}

export function AddReminderSheet({ open, onOpenChange, childId, familyId, allChildren }: Props) {
  const { timezone } = useFamily();
  const queryClient = useQueryClient();
  const [saving, setSaving] = useState(false);

  const [selectedChildIds, setSelectedChildIds] = useState<Set<string>>(new Set([childId]));
  const showMultiSelect = allChildren && allChildren.length > 1;

  function toggleChild(id: string) {
    setSelectedChildIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        if (next.size > 1) next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  function getDefaultDateAndTime() {
    const now = new Date();
    const today = new Date(now);
    today.setHours(0, 0, 0, 0);
    const in30 = new Date(now.getTime() + 30 * 60 * 1000);
    const timeValue = `${String(in30.getHours()).padStart(2, "0")}:${String(in30.getMinutes()).padStart(2, "0")}`;
    return { date: today, timeValue };
  }

  const defaults = getDefaultDateAndTime();
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("info");
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(() => defaults.date);
  const [timeValue, setTimeValue] = useState(() => defaults.timeValue);
  const [useRemindBefore, setUseRemindBefore] = useState(true);
  const [beforeMinutes, setBeforeMinutes] = useState(15);
  const [useRemindAfter, setUseRemindAfter] = useState(false);
  const [afterMinutes, setAfterMinutes] = useState(30);

  // Recurrence state — unified
  const [recurrenceEnabled, setRecurrenceEnabled] = useState(false);
  const [recurrenceType, setRecurrenceType] = useState<"daily" | "weekly">("daily");
  const [weeklyDays, setWeeklyDays] = useState<number[]>([1, 2, 3, 4, 5]);

  const [dayTimes, setDayTimes] = useState<Record<number, string>>({});
  const [perDayTime, setPerDayTime] = useState(false);

  const repeatType: RepeatType = recurrenceEnabled ? recurrenceType : "none";

  useEffect(() => {
    if (open) {
      const { date, timeValue: t } = getDefaultDateAndTime();
      setSelectedDate(date);
      setTimeValue(t);
    }
  }, [open]);

  function resetForm() {
    setTitle("");
    setCategory("info");
    const { date, timeValue: t } = getDefaultDateAndTime();
    setSelectedDate(date);
    setTimeValue(t);
    setUseRemindBefore(true);
    setBeforeMinutes(15);
    setUseRemindAfter(false);
    setAfterMinutes(30);
    setRecurrenceEnabled(false);
    setRecurrenceType("daily");
    setWeeklyDays([1, 2, 3, 4, 5]);
    setDayTimes({});
    setPerDayTime(false);
    setSelectedChildIds(new Set([childId]));
  }

  function timeMinusMinutes(timeStr: string, minutes: number): string {
    const [h, m] = timeStr.split(":").map(Number);
    let totalM = h * 60 + m - minutes;
    totalM = ((totalM % (24 * 60)) + 24 * 60) % (24 * 60);
    const nh = Math.floor(totalM / 60);
    const nm = totalM % 60;
    return `${String(nh).padStart(2, "0")}:${String(nm).padStart(2, "0")}`;
  }

  async function handleSave() {
    if (!title.trim()) {
      toast.error("Skriv inn en tittel");
      return;
    }
    if (!useRemindBefore && !useRemindAfter) {
      toast.error("Velg påminnelse før og/eller etter");
      return;
    }
    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Ikke innlogget");

      const targetChildren = showMultiSelect
        ? allChildren.filter((c) => selectedChildIds.has(c.id))
        : [{ id: childId, family_id: familyId }];

      const rows: Array<{
        child_id: string;
        family_id: string;
        title: string;
        category: string;
        remind_date: string | null;
        remind_at: string | null;
        repeat_rule: string | null;
        created_by: string;
      }> = [];

      const activeDays = getDaysForRepeatLegacy(repeatType, weeklyDays);
      const effectiveDayTimes: Record<number, string> = {};
      if (recurrenceEnabled) {
        for (const d of activeDays) {
          effectiveDayTimes[d] = perDayTime ? (dayTimes[d] || timeValue) : timeValue;
        }
      }

      // Build a boolean[] for legacy buildRepeatRule compatibility
      const customDaysBool = [false, false, false, false, false, false, false];
      for (const d of weeklyDays) {
        if (d >= 1 && d <= 7) customDaysBool[d - 1] = true;
      }

      if (useRemindBefore) {
        const beforeTimeValue = timeMinusMinutes(timeValue, beforeMinutes);
        const effectiveDayTimesBefore: Record<number, string> = {};
        if (recurrenceEnabled) {
          for (const d of activeDays) {
            effectiveDayTimesBefore[d] = timeMinusMinutes(effectiveDayTimes[d] || timeValue, beforeMinutes);
          }
        }
        const repeatRuleBefore = buildRepeatRule(repeatType, weeklyDays, true, effectiveDayTimesBefore, 15);
        const remindDateBefore = selectedDate ? getDateString(selectedDate, timezone) : null;
        const remindAtBefore = remindDateBefore ? `${remindDateBefore}T${beforeTimeValue}:00` : null;
        for (const child of targetChildren) {
          rows.push({
            child_id: child.id,
            family_id: child.family_id,
            title: title.trim(),
            category,
            remind_date: recurrenceEnabled ? null : remindDateBefore,
            remind_at: recurrenceEnabled ? null : remindAtBefore,
            repeat_rule: repeatRuleBefore,
            created_by: user.id,
          });
        }
      }

      if (useRemindAfter) {
        const repeatRuleAfter = buildRepeatRule(repeatType, weeklyDays, true, effectiveDayTimes, afterMinutes);
        const remindDateAfter = selectedDate ? getDateString(selectedDate, timezone) : null;
        const remindAtAfter = remindDateAfter ? `${remindDateAfter}T${timeValue}:00` : null;
        for (const child of targetChildren) {
          rows.push({
            child_id: child.id,
            family_id: child.family_id,
            title: title.trim(),
            category,
            remind_date: recurrenceEnabled ? null : remindDateAfter,
            remind_at: recurrenceEnabled ? null : remindAtAfter,
            repeat_rule: repeatRuleAfter,
            created_by: user.id,
          });
        }
      }

      const { data: inserted, error } = await supabase.from("reminders").insert(rows).select("id, child_id, family_id");

      if (error) throw error;

      if (recurrenceEnabled && rows.some((r) => r.repeat_rule)) {
        const now = new Date();
        const todayLocal = getDateString(now, timezone);
        const todayDate = new Date(now.toLocaleString("en-US", { timeZone: timezone }));
        const isoDay = todayDate.getDay() === 0 ? 7 : todayDate.getDay();
        const days = activeDays;

        if (repeatType === "daily" || days.includes(isoDay)) {
          const insertedList = inserted ?? [];
          for (let i = 0; i < insertedList.length; i++) {
            if (rows[i]?.repeat_rule) {
              await supabase
                .from("reminder_occurrences")
                .upsert(
                  { reminder_id: insertedList[i].id, occurrence_date: todayLocal },
                  { onConflict: "reminder_id,occurrence_date" }
                );
            }
          }
        }
      }

      for (const row of (inserted ?? [])) {
        logActivity({ familyId: row.family_id, childId: row.child_id, action: "created", entityType: "reminder", entityId: row.id });
      }

      const reminderCount = rows.length;
      const childCount = targetChildren.length;
      if (reminderCount > 1 && childCount > 1) {
        toast.success(`${reminderCount} påminnelser opprettet for ${childCount} barn`);
      } else if (childCount > 1) {
        toast.success(`Påminnelse opprettet for ${childCount} barn`);
      } else {
        toast.success(reminderCount > 1 ? `${reminderCount} påminnelser opprettet` : "Påminnelse opprettet");
      }
      for (const child of targetChildren) {
        queryClient.invalidateQueries({ queryKey: ["reminders", child.id] });
      }
      resetForm();
      onOpenChange(false);
    } catch (err: any) {
      toast.error(err.message || "Kunne ikke opprette påminnelse");
    } finally {
      setSaving(false);
    }
  }

  const selectedCategoryEmoji = CATEGORIES.find((c) => c.value === category)?.emoji || "📋";

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="rounded-t-2xl max-h-[90vh] overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="text-lg font-display">Ny påminnelse</SheetTitle>
          <SheetDescription className="text-sm text-muted-foreground">
            Opprett en påminnelse for barnet
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-5 mt-4 pb-4">
          {/* Multi-child selector */}
          {showMultiSelect && (
            <div className="space-y-1.5">
              <Label className="text-sm">Barn</Label>
              <div className="flex flex-wrap gap-2">
                {allChildren.map((child) => (
                  <button
                    key={child.id}
                    type="button"
                    onClick={() => toggleChild(child.id)}
                    className={cn(
                      "flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-sm transition-colors",
                      selectedChildIds.has(child.id)
                        ? "border-primary bg-primary/10 text-foreground"
                        : "border-border bg-background text-muted-foreground hover:bg-muted"
                    )}
                  >
                    <span>{child.avatar_emoji}</span>
                    <span>{child.name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Title */}
          <div className="space-y-1.5">
            <Label htmlFor="reminder-title">Tittel</Label>
            <div className="flex items-center gap-2">
              <span className="text-xl">{selectedCategoryEmoji}</span>
              <Input
                id="reminder-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="F.eks. Ta med medisin"
                className="flex-1"
                onFocus={(e) => setTimeout(() => e.target.scrollIntoView({ behavior: "smooth", block: "center" }), 300)}
              />
            </div>
          </div>

          {/* Category */}
          <div className="space-y-1.5">
            <Label>Kategori</Label>
            <div className="flex flex-wrap gap-1.5">
              {CATEGORIES.map((cat) => (
                <Badge
                  key={cat.value}
                  variant={category === cat.value ? "default" : "outline"}
                  className={cn(
                    "cursor-pointer text-xs px-2.5 py-1 transition-colors",
                    category === cat.value
                      ? "bg-primary text-primary-foreground"
                      : "hover:bg-muted"
                  )}
                  onClick={() => setCategory(cat.value)}
                >
                  {cat.emoji} {cat.label}
                </Badge>
              ))}
            </div>
          </div>

          {/* Date & time — shared component */}
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">Velg når det skal skje (f.eks. avreise, medisin)</p>
            <DateTimeInput
              date={selectedDate}
              onDateChange={setSelectedDate}
              showDate={!recurrenceEnabled}
              time={timeValue}
              onTimeChange={setTimeValue}
              disablePast
            />

            {/* Påminn før og/eller etter */}
            <div className="space-y-3 rounded-lg border border-border/60 p-3">
              <Label className="text-xs font-medium">Påminn</Label>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="remind-before"
                    checked={useRemindBefore}
                    onCheckedChange={(c) => setUseRemindBefore(!!c)}
                  />
                  <Label htmlFor="remind-before" className="cursor-pointer text-sm shrink-0 w-8">Før</Label>
                  <Select
                    value={String(beforeMinutes)}
                    onValueChange={(v) => setBeforeMinutes(Number(v))}
                    disabled={!useRemindBefore}
                  >
                    <SelectTrigger className="h-8 text-xs flex-1 min-w-0">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="5">5 min</SelectItem>
                      <SelectItem value="15">15 min</SelectItem>
                      <SelectItem value="30">30 min</SelectItem>
                      <SelectItem value="60">60 min</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="remind-after"
                    checked={useRemindAfter}
                    onCheckedChange={(c) => setUseRemindAfter(!!c)}
                  />
                  <Label htmlFor="remind-after" className="cursor-pointer text-sm shrink-0 w-10">Etter</Label>
                  <Select
                    value={String(afterMinutes)}
                    onValueChange={(v) => setAfterMinutes(Number(v))}
                    disabled={!useRemindAfter}
                  >
                    <SelectTrigger className="h-8 text-xs flex-1 min-w-0">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">Ved tidspunkt</SelectItem>
                      <SelectItem value="15">15 min</SelectItem>
                      <SelectItem value="30">30 min</SelectItem>
                      <SelectItem value="60">60 min</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {!useRemindBefore && !useRemindAfter && (
                <p className="text-xs text-destructive">Velg minst én: før og/eller etter</p>
              )}
            </div>

            {/* Per-day time toggle – only when repeating */}
            {recurrenceEnabled && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={perDayTime}
                    onCheckedChange={setPerDayTime}
                    className="scale-90"
                  />
                  <Label className="text-xs text-muted-foreground">Ulikt tidspunkt per dag</Label>
                </div>

                {perDayTime && (
                  <div className="space-y-1.5 rounded-lg bg-muted/50 p-2">
                    {getDaysForRepeatLegacy(repeatType, weeklyDays).map((dayNum) => (
                      <div key={dayNum} className="flex items-center justify-between gap-2">
                        <span className="text-xs font-medium w-10">{DAY_LABELS[dayNum - 1]}</span>
                        <Input
                          type="time"
                          value={dayTimes[dayNum] || timeValue}
                          onChange={(e) =>
                            setDayTimes((prev) => ({ ...prev, [dayNum]: e.target.value }))
                          }
                          className="w-28 h-8 text-xs"
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Recurrence — shared component */}
          <RecurrenceInput
            enabled={recurrenceEnabled}
            onEnabledChange={setRecurrenceEnabled}
            type={recurrenceType}
            onTypeChange={setRecurrenceType}
            weeklyDays={weeklyDays}
            onWeeklyDaysChange={setWeeklyDays}
          />

          {/* Save */}
          <Button
            onClick={handleSave}
            disabled={saving || !title.trim() || (!useRemindBefore && !useRemindAfter)}
            className="w-full rounded-xl"
          >
            {saving ? "Lagrer…" : "Opprett påminnelse"}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
